import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { castVote, removeVote } from '@/lib/api/votes';
import type { Snap } from '@/types/database.types';

interface UseVoteStateProps {
  initialSnap: Snap | null;
  onVoteSuccess?: (updatedSnap: Snap) => void;
}

export function useVoteState({ initialSnap, onVoteSuccess }: UseVoteStateProps) {
  const [snap, setSnap] = useState<Snap | null>(initialSnap);
  const [isVoting, setIsVoting] = useState(false);

  // Calculate percentage for an option
  const calculatePercentage = (optionId: string): number => {
    if (!snap) return 0;
    const option = snap.options?.find(opt => opt.id === optionId);
    if (!option || snap.total_votes === 0) return 0;
    return (option.vote_count / snap.total_votes) * 100;
  };

  // Handle voting on an option
  const handleVote = async (optionId: string) => {
    if (!snap) return;
    
    // Prevent multiple votes while a vote is in progress
    if (isVoting) return;
    
    // If already voted for this option, do nothing
    if (snap.userVote === optionId) {
      toast.error('You have already voted for this option');
      return;
    }
    
    try {
      setIsVoting(true);
      
      // Call the API to cast the vote
      await castVote(snap.id, optionId);

      // Calculate if this is a new vote or changing an existing vote
      const isChangingVote = snap.userVote !== null;
      
      // Create updated options with new vote counts
      const updatedOptions = snap.options?.map(option => {
        if (option.id === optionId) {
          // Increment the new option's vote count
          return { ...option, vote_count: option.vote_count + 1 };
        }
        if (option.id === snap.userVote) {
          // Decrement the previous option's vote count if changing vote
          return { ...option, vote_count: Math.max(0, option.vote_count - 1) };
        }
        return option;
      }) || [];

      // Calculate the new total votes
      // Only increment if this is a new vote, not a vote change
      const newTotalVotes = isChangingVote 
        ? snap.total_votes 
        : snap.total_votes + 1;

      // Create the updated snap object
      const updatedSnap: Snap = {
        ...snap,
        userVote: optionId,
        options: updatedOptions,
        total_votes: newTotalVotes
      };

      // Update local state
      setSnap(updatedSnap);
      
      // Call the success callback if provided
      if (onVoteSuccess) {
        onVoteSuccess(updatedSnap);
      }

      // Show success message
      toast.success(isChangingVote ? 'Vote changed successfully!' : 'Vote cast successfully!');
    } catch (error) {
      console.error('Error casting vote:', error);
      toast.error('Failed to cast vote');
    } finally {
      setIsVoting(false);
    }
  };

  // Handle removing a vote
  const handleRemoveVote = async () => {
    if (!snap || !snap.userVote) return;
    
    // Prevent multiple operations while a vote is in progress
    if (isVoting) return;
    
    try {
      setIsVoting(true);
      
      // Call the API to remove the vote
      await removeVote(snap.id);

      // Find the option that was voted for
      const votedOption = snap.options?.find(opt => opt.id === snap.userVote);
      
      if (!votedOption) {
        throw new Error('Voted option not found');
      }
      
      // Create updated options with new vote counts
      const updatedOptions = snap.options?.map(option => {
        if (option.id === snap.userVote) {
          // Decrement the vote count for the previously voted option
          return { ...option, vote_count: Math.max(0, option.vote_count - 1) };
        }
        return option;
      }) || [];

      // Calculate the new total votes (decrement by 1)
      const newTotalVotes = Math.max(0, snap.total_votes - 1);

      // Create the updated snap object
      const updatedSnap: Snap = {
        ...snap,
        userVote: null,
        options: updatedOptions,
        total_votes: newTotalVotes
      };

      // Update local state
      setSnap(updatedSnap);
      
      // Call the success callback if provided
      if (onVoteSuccess) {
        onVoteSuccess(updatedSnap);
      }

      // Show success message
      toast.success('Vote removed successfully!');
    } catch (error) {
      console.error('Error removing vote:', error);
      toast.error('Failed to remove vote');
    } finally {
      setIsVoting(false);
    }
  };

  return {
    snap,
    setSnap,
    calculatePercentage,
    handleVote,
    handleRemoveVote,
    isVoted: (optionId: string) => snap?.userVote === optionId,
    isVoting,
    hasVoted: snap?.userVote !== null
  };
}